/**
 * RotationController — the rotation machinery (line + image + group +
 * pivot handles) extracted from ConstructionTool (#835, epic #828). A
 * tool OWNS a controller instance and DRIVES it by composition (it is
 * NOT a base class), mirroring the `DrawGestureEngine` pattern: the
 * controller owns the rotation drag STATE and the hit-testing / state
 * machines, while everything construction-specific it needs from the
 * owning tool is injected through the `RotationHost` interface.
 *
 * The controller owns four drag-state fields exclusively:
 *   - `pivotDrag`          — the draggable rotation-pivot crosshair (#420);
 *   - `lineRotationDrag`   — single-line rotation (#67);
 *   - `groupRotationDrag`  — construction-group rotation;
 *   - `imageRotationDrag`  — tracing-image rotation (#526).
 *
 * Rotation MATH lives in `src/model/constructionRotation.ts` (composed
 * rotations, partner translations, pivot resolution) and is left there;
 * this controller is the interaction layer that reads pointer input,
 * runs the live-snap pipeline, and commits through the model ops.
 *
 * ConstructionTool's `onDown` / `onMove` / `onHover` / `onUp` /
 * `onCancel` delegate the rotation cases to this controller.
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene } from "../canvas/Scene";
import type { ToolSettings } from "./EditorContext";
import {
  projectConstructionLines,
  projectTracingImages,
  tracingImageCenter,
  tracingImageRotation,
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  type ConstructionLine,
  type TracingImage,
  type Constraint,
  type Project,
} from "../model/Project";
import {
  findNearestSnapCandidate,
  hasCoincidentConstraint,
  type AnchorRefLite,
  type SnapRefLite,
} from "../model/constraintEnforcement";
import { withSnappedPartnersFollowing } from "../model/constructionLineNormalize";
import { isMirrorDerived } from "../model/constructionMirror";
import { routeMirrorRotationToSource } from "../model/connections";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isPointConstraintActive,
  isPointSnapActive,
} from "./dragSnap";
import {
  composeRotations,
  displayedBboxCenter,
  effectiveGroupPivot,
  effectiveImagePivot,
  effectiveLinePivot,
  getUnrotatedBounds,
  getUnrotatedGroupBounds,
  normalizeRotation,
  rotateConstructionLine,
  rotationPartnerTranslations,
  translateLineForRotationPartner,
  worldToLineLocal,
} from "../model/constructionRotation";
import { rotateTracingImage } from "../model/tracingImageTransform";
import type { ResizeHandle } from "../model/constructionResize";
import { isOnRotationKnob, unrotatePoint } from "../model/transformHandles";
import { rotationSweepRays } from "./rotationSweep";
import { newId } from "../model/ids";
import { setCanvasCursor } from "../canvas/canvasCursor";
import { setAngleHint } from "../snapAngleIndicator";
import { showCanvasToast } from "../canvasToast";
import {
  isPinnableSnapRef,
  snapRefToConstraintRef,
  MIRROR_OFFSET_REFUSE_HINT,
} from "./constructionSnapRefs";
import {
  hitRadiusFor,
  snapRadiusFor,
  COARSE_HIT_SCALE,
  type PointerKind,
} from "../canvas/pointerPrecision";

/** The per-frame (source ↔ candidate) snap pair shared with the owning
 *  tool. Structurally identical to ConstructionTool's `liveSnap` field
 *  so reads / writes stay in lock-step across the extraction boundary. */
export interface RotationLiveSnap {
  source: SnapRefLite;
  candidate: SnapRefLite;
  candidatePos: { x: number; y: number };
}

/** Everything the RotationController needs from its owning tool. The
 *  four rotation drag-state fields live in the controller; every other
 *  collaborator (scope / scene / settings / project, the shared
 *  live-snap machinery, the resize interplay, and the other concerns'
 *  drag guards) is injected through this host. */
export interface RotationHost {
  // ---- core context -------------------------------------------------
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getSettings(): ToolSettings;
  /** The current project state (ConstructionTool's `ctx.history.state`). */
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  rerender(): void;

  // ---- selection lookups shared with non-rotation code --------------
  /** Id of the sole selected non-offset construction line, or null. */
  rotatableSelectedLineId(): string | null;

  // ---- resize interplay (owned by the tool, stays there) ------------
  /** Id of the single line currently showing corner RESIZE handles
   *  (#388), or null — its corner ring is given over to resize so
   *  rotation skips it. */
  resizableSelectedLineId(): string | null;
  /** Which resize handle (corner or edge, #863) `p` falls on for the selected
   *  line, or null. Used by the line rotation hover to yield to resize. */
  resizeHandleAt(p: { x: number; y: number }): { handle: ResizeHandle } | null;
  /** Resize cursor for a handle (corner or edge). */
  resizeCursorFor(handle: ResizeHandle): string;

  // ---- other concerns' drag guards ----------------------------------
  isResizeDragActive(): boolean;
  isLineDragActive(): boolean;
  isGroupDragActive(): boolean;
  isLassoActive(): boolean;

  // ---- shared live-snap machinery -----------------------------------
  getLiveSnap(): RotationLiveSnap | null;
  setLiveSnap(snap: RotationLiveSnap | null): void;
  clearLiveSnap(): void;
  refreshLiveSnapOverlay(): void;
  snapRadiusMm(): number;
}

export class RotationController {
  // ---- rotation constants (interaction geometry) --------------------
  /** Grab radius for the rotation knob, in screen px at zoom 1.
   *
   *  Was 2 — the rendered knob radius exactly, with no grace margin, because
   *  the knob sits close enough to other construction geometry (offset lines,
   *  neighbours) that a generous zone silently steals drags meant for those
   *  (#67). Doubled to 4 on all platforms: matching the DRAWN radius made the
   *  knob as hard to hit as it is small, and 4 px is still well inside the
   *  ~9 px the neighbouring geometry needs to stay reachable. A finger gets
   *  `COARSE_HIT_SCALE` on top of this. */
  private static readonly ROTATION_HANDLE_HIT_RADIUS_MM = 4;
  /** What is pointing right now, set from the press before any hit test runs
   *  (#40). A finger grabs {@link COARSE_HIT_SCALE}× wider: 2 px of grab is
   *  unreachable with a fingertip, and missing it does nothing at all rather
   *  than something wrong. The #67 caution above is about a MOUSE — widening
   *  a precise pointer's zone steals drags meant for neighbouring geometry —
   *  and a finger has no precise alternative to steal from. */
  private pointerKind: PointerKind = "mouse";
  /** Corner rotation hot zone sits flush against the bbox corner
   *  (inner = 0) so the gesture is discoverable right at the point
   *  rather than buried in an empty inner ring; outer kept tight so
   *  it doesn't compete with normal line drags a few mm out. */
  private static readonly ROTATION_CORNER_INNER_MM = 0;
  private static readonly ROTATION_CORNER_OUTER_MM = 5;
  private static readonly ROTATION_SHIFT_SNAP_DEG = 15;

  /** Snap radius for the draggable rotation pivot — generous enough
   *  to latch onto anchors / the bbox centre without feeling sticky. */
  private static readonly PIVOT_SNAP_RADIUS_MM = 3;

  /** Hit radius for grabbing the always-on pivot crosshair. Kept just
   *  above the rendered half-length so the clickable zone tracks the
   *  visible `+` without a margin that steals drags from neighbours. */
  private static readonly PIVOT_HIT_RADIUS_MM = 3;

  // ---- exclusively-owned drag state ---------------------------------

  /** Pivot drag (issue #420) — engaged when the user grabs the
   *  always-on rotation pivot crosshair to reposition it. `pivot` is
   *  the live (snapped) position updated each frame; `snapped`
   *  records whether the current position latched onto an anchor /
   *  the bbox centre (drives a future highlight). `start` is the
   *  pivot position at drag start, used for the no-op guard. */
  pivotDrag: {
    owner:
      | { kind: "line"; lineId: string }
      | { kind: "group"; groupId: string }
      | { kind: "image"; imageId: string };
    start: { x: number; y: number };
    pivot: { x: number; y: number };
    snapped: boolean;
  } | null = null;

  /** Rotation drag (issue #67) — engaged when the user grabs the
   *  top-of-bbox rotation knob, presses in the diagonal-quadrant ring
   *  just outside a bbox corner, or grabs an arbitrary anchor on the
   *  line. The drag spins the line about `(cx, cy)`; release commits
   *  via `rotateConstructionLine`. `trackRotation` is true only when
   *  the pivot is the line's natural bbox centre (top handle / bbox
   *  corner) — for arbitrary-anchor rotations we bake the result
   *  into pathData and reset the `rotation` field, since the
   *  bbox-tilt invariant breaks once rotation pivots drift. */
  lineRotationDrag: {
    constructionId: string;
    cx: number;
    cy: number;
    startAngle: number;
    appliedDeg: number;
    last: { x: number; y: number };
    /** Which entry point fired the drag — drives the cursor (move
     *  vs grabbing) and the visual hint geometry. */
    source: "handle" | "corner";
    trackRotation: boolean;
    /** Bidirectional editing (issue #180): when the gesture started
     *  on a mirror-derived line, `constructionId` was swapped to the
     *  source root and `cx, cy` are the pivot in source-space. We
     *  still need to measure the cursor's rotation angle against
     *  the DERIVED pivot the user clicked, then sign-flip the
     *  resulting delta per line-reflection in the chain before
     *  driving `setTransientLineRotation` / `rotateConstructionLine`
     *  on the source root. `mirrorId` is the line the user clicked
     *  on — Scene needs it to rotate the mirror's overlay items
     *  (bbox + knob) in lock-step with the cursor; without this the
     *  path tracks the cursor live but the bbox stays frozen until
     *  commit. */
    bidirectionalMirror?: {
      mirrorId: string;
      derivedPivot: { x: number; y: number };
      sign: 1 | -1;
    };
  } | null = null;

  /** Group-level rotation drag — same shape as `lineRotationDrag`
   *  but the rotation applies to every descendant line of the
   *  targeted construction group. Tracking the per-line `rotation`
   *  field doesn't make sense here (the group has its own coordinate
   *  frame), so the commit bakes the rotation into each line's
   *  pathData and clears their `rotation` fields. */
  groupRotationDrag: {
    constructionGroupId: string;
    cx: number;
    cy: number;
    startAngle: number;
    appliedDeg: number;
    last: { x: number; y: number };
    source: "handle" | "corner";
  } | null = null;

  /** Tracing-image rotation drag (issue #526) — grabbing the knob above
   *  a sole-selected image spins it about its centre, the same gesture
   *  a construction line gets. `cx`/`cy` is the image centre; `startDeg`
   *  is the committed angle; `startPointerAngle` is the cursor's angle
   *  to the centre at grab. */
  imageRotationDrag: {
    id: string;
    cx: number;
    cy: number;
    startDeg: number;
    startPointerAngle: number;
    lastDeg: number;
  } | null = null;

  constructor(private host: RotationHost) {}

  /** Current view zoom (project units per screen px = 1 / zoom). The
   *  rotation knob / corner-zone / pivot constants above are "screen px
   *  at zoom 1"; dividing them by this keeps the hit targets the same
   *  on-screen size as the (now screen-constant) rendered handles at
   *  every zoom level (#864). */
  private zoom(): number {
    const z = this.host.getScope().view?.zoom;
    return z && z > 0 ? z : 1;
  }

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** True while any rotation / pivot drag is in flight. */
  hasActiveDrag(): boolean {
    return !!(
      this.pivotDrag ||
      this.lineRotationDrag ||
      this.groupRotationDrag ||
      this.imageRotationDrag
    );
  }

  /** onMove delegation: handle whichever rotation / pivot drag is
   *  active. Returns true when a drag consumed the move. Mirrors the
   *  original in-order dispatch (pivot → group → image → line). */
  handleMove(p: { x: number; y: number }, s: PointerSample): boolean {
    if (this.pivotDrag) {
      this.updatePivotDrag(p, angleModifierHeld(s));
      return true;
    }
    if (this.groupRotationDrag) {
      this.updateGroupRotationDrag(p, angleModifierHeld(s));
      return true;
    }
    if (this.imageRotationDrag) {
      this.updateImageRotationDrag(p, angleModifierHeld(s));
      return true;
    }
    if (this.lineRotationDrag) {
      this.updateLineRotationDrag(p, angleModifierHeld(s));
      return true;
    }
    return false;
  }

  /** onUp delegation for the pivot drag (which the tool dispatches
   *  before its alt-duplicate / bezier-release handling). Returns true
   *  when the pivot drag was committed. */
  handlePivotUp(p: { x: number; y: number }, s: PointerSample): boolean {
    if (!this.pivotDrag) return false;
    this.updatePivotDrag(p, angleModifierHeld(s));
    this.commitPivotDrag();
    return true;
  }

  /** onUp delegation for the line / group / image rotation drags.
   *  Returns true when one of them was committed. */
  handleRotationUp(p: { x: number; y: number }, s: PointerSample): boolean {
    if (this.groupRotationDrag) {
      this.updateGroupRotationDrag(p, angleModifierHeld(s));
      this.commitGroupRotationDrag();
      return true;
    }
    if (this.imageRotationDrag) {
      this.updateImageRotationDrag(p, angleModifierHeld(s));
      this.commitImageRotationDrag();
      return true;
    }
    if (this.lineRotationDrag) {
      this.updateLineRotationDrag(p, angleModifierHeld(s));
      this.commitLineRotationDrag();
      return true;
    }
    return false;
  }

  /** onHover delegation (select sub-mode). Returns true when the pivot
   *  crosshair claimed the cursor (caller should stop). Otherwise it
   *  runs the group / line / image rotation-handle hovers and returns
   *  false, matching the original fall-through. */
  handleHover(p: { x: number; y: number }): boolean {
    // Pivot crosshair hover wins over every rotation-handle hover —
    // it's a small deliberate target sitting at the bbox centre
    // (issue #420). Only when not over the pivot do the rotation
    // handles get a chance to claim the cursor.
    if (this.pivotHandleAt(p)) {
      setCanvasCursor(this.host.getScope(), "grab");
      return true;
    }
    this.updateGroupRotationHover(p);
    // Skip per-line / per-image hover when the group hover already
    // claimed the cursor — keeps them from fighting over cursor state
    // when a group is selected and a member is also visible. A
    // sole-selected image gets the same rotate-knob "grab" cursor as a
    // line (issue #545); the two selections are mutually exclusive
    // (`rotatableSelectedImageId` requires no lines selected), so the
    // image hover is a no-op whenever the line hover would fire.
    if (!this.host.getScene().singleSelectedConstructionGroupId()) {
      this.updateLineRotationHover(p);
      this.updateImageRotationHover(p);
    }
    return false;
  }

  /** onCancel delegation: drop every in-flight rotation / pivot drag. */
  handleCancel(): void {
    if (this.lineRotationDrag) this.cancelLineRotationDrag();
    if (this.groupRotationDrag) this.cancelGroupRotationDrag();
    if (this.imageRotationDrag) {
      // Drop the in-flight spin and re-render at the committed angle.
      this.imageRotationDrag = null;
      this.host.getScene().render(this.host.getProject());
    }
    if (this.pivotDrag) this.cancelPivotDrag();
  }

  // -------------------------------------------------------------------
  // Line rotation
  // -------------------------------------------------------------------

  private isOnLineRotationHandle(
    line: ConstructionLine,
    bounds: { x: number; y: number; width: number; height: number },
    p: { x: number; y: number },
  ): boolean {
    // Screen-constant knob position + hit radius: both are "screen px at
    // zoom 1" divided by the live zoom, matching the rendered stem/knob. The
    // knob's POSITION comes from the shared geometry the renderer and leather's
    // frame also read, so the three can't drift apart again (#990).
    return isOnRotationKnob(bounds, worldToLineLocal(line, p), {
      zoom: this.zoom(),
      radiusMm: hitRadiusFor(
        RotationController.ROTATION_HANDLE_HIT_RADIUS_MM,
        this.pointerKind,
      ),
    });
  }

  private lineCornerRotationZone(
    line: ConstructionLine,
    bounds: { x: number; y: number; width: number; height: number },
    p: { x: number; y: number },
  ): "tl" | "tr" | "bl" | "br" | null {
    const local = worldToLineLocal(line, p);
    const zoom = this.zoom();
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;
    const corners: {
      key: "tl" | "tr" | "bl" | "br";
      x: number;
      y: number;
    }[] = [
      { key: "tl", x: bounds.x, y: bounds.y },
      { key: "tr", x: bounds.x + bounds.width, y: bounds.y },
      { key: "bl", x: bounds.x, y: bounds.y + bounds.height },
      { key: "br", x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    ];
    for (const c of corners) {
      const dx = local.x - c.x;
      const dy = local.y - c.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (
        dist < RotationController.ROTATION_CORNER_INNER_MM / zoom ||
        dist >
          hitRadiusFor(
            RotationController.ROTATION_CORNER_OUTER_MM,
            this.pointerKind,
          ) /
            zoom
      ) {
        continue;
      }
      const outsideX =
        c.x === centerX ? false : (local.x - c.x) * (c.x - centerX) > 0;
      const outsideY =
        c.y === centerY ? false : (local.y - c.y) * (c.y - centerY) > 0;
      if (outsideX && outsideY) return c.key;
    }
    return null;
  }

  /** The sole-selected tracing image whose rotation knob is grabbable,
   *  or null. Mirrors `rotatableSelectedLineId`: the handle is only an
   *  affordance for a single-image selection (no lines / groups). */
  private rotatableSelectedImageId(): string | null {
    const sel = this.host.getScene().getSelection();
    const imageIds = sel.tracingImageIds ?? [];
    if (imageIds.length !== 1) return null;
    if ((sel.constructionIds ?? []).length > 0) return null;
    if ((sel.constructionGroupIds ?? []).length > 0) return null;
    return imageIds[0]!;
  }

  /** True when `p` (project mm) lands on the image's rotation knob —
   *  the cursor is rotated back into the image's un-rotated local frame
   *  (about its centre) and checked against the knob's un-rotated
   *  position (top-centre, offset above the bbox), mirroring
   *  `isOnLineRotationHandle`. */
  private isOnImageRotationHandle(
    image: TracingImage,
    p: { x: number; y: number },
  ): boolean {
    const local = unrotatePoint(
      p,
      tracingImageCenter(image),
      tracingImageRotation(image),
    );
    // Screen-constant knob position + hit radius (both / zoom), from the
    // shared geometry (#990).
    return isOnRotationKnob(
      {
        x: image.x,
        y: image.y,
        width: image.widthMm,
        height: image.heightMm,
      },
      local,
      {
        zoom: this.zoom(),
        radiusMm: hitRadiusFor(
          RotationController.ROTATION_HANDLE_HIT_RADIUS_MM,
          this.pointerKind,
        ),
      },
    );
  }

  /** Hover-cursor feedback for a sole-selected image's rotation knob
   *  (issue #545) — the image analogue of `updateLineRotationHover` /
   *  `updateGroupRotationHover`. Shows the "grab" cursor over the knob so
   *  the rotate affordance reads the same as it does for lines and groups.
   *  Returns true when it claimed the cursor. */
  private updateImageRotationHover(p: { x: number; y: number }): boolean {
    if (
      this.host.isResizeDragActive() ||
      this.lineRotationDrag ||
      this.imageRotationDrag ||
      this.groupRotationDrag ||
      this.host.isLineDragActive() ||
      this.host.isGroupDragActive() ||
      this.host.isLassoActive()
    ) {
      return false;
    }
    const id = this.rotatableSelectedImageId();
    if (!id) return false;
    const image = projectTracingImages(this.host.getProject()).find(
      (img) => img.id === id,
    );
    if (!image || !this.isOnImageRotationHandle(image, p)) return false;
    setCanvasCursor(this.host.getScope(), "grab");
    return true;
  }

  maybeStartImageRotationDrag(
    p: { x: number; y: number },
    _shiftKey: boolean,
  ): boolean {
    const id = this.rotatableSelectedImageId();
    if (!id) return false;
    const image = projectTracingImages(this.host.getProject()).find(
      (img) => img.id === id,
    );
    if (!image || !this.isOnImageRotationHandle(image, p)) return false;
    // Spin about the effective pivot (#420) — the user-positioned pivot
    // when set, else the image centre. An off-centre pivot makes the image
    // orbit as it turns, exactly like a line / group.
    const c = effectiveImagePivot(image);
    this.imageRotationDrag = {
      id,
      cx: c.x,
      cy: c.y,
      startDeg: tracingImageRotation(image),
      startPointerAngle: Math.atan2(p.y - c.y, p.x - c.x),
      lastDeg: tracingImageRotation(image),
    };
    return true;
  }

  maybeStartLineRotationDrag(
    p: { x: number; y: number },
    shiftKey: boolean,
  ): boolean {
    const scope = this.host.getScope();
    // Top handle is selection-anchored — only meaningful when the
    // user has already picked a single line.
    const selectedId = this.host.rotatableSelectedLineId();
    if (selectedId) {
      const selectedLine = projectConstructionLines(
        this.host.getProject(),
      ).find((l) => l.id === selectedId);
      if (selectedLine) {
        const bounds = getUnrotatedBounds(selectedLine, scope);
        if (bounds && this.isOnLineRotationHandle(selectedLine, bounds, p)) {
          this.startLineRotationDrag(selectedId, p, shiftKey, "handle");
          return true;
        }
      }
    }
    // Line corner rotation was removed in #388 — a line's bbox corners
    // now resize the selected line (maybeStartResizeDrag, which runs
    // first in onDown). Rotation stays on the top handle (above); the
    // anchor-pivot rotation gesture was replaced by the draggable
    // pivot crosshair (issue #420).
    return false;
  }

  /** Search every visible, non-offset construction line for a corner
   *  whose bbox-adjacent diagonal-quadrant ring contains `p`.
   *  Returns the closest match (by distance to the corner) so two
   *  near-overlapping lines don't both claim the gesture. */
  private findCornerRotationHit(p: {
    x: number;
    y: number;
  }): { lineId: string; corner: "tl" | "tr" | "bl" | "br" } | null {
    const scope = this.host.getScope();
    // The selected resizable line gives its corners over to resize
    // handles (issue #388) — skip it so its corner ring no longer
    // rotates. Other lines keep the discoverable corner-rotation ring.
    const resizableId = this.host.resizableSelectedLineId();
    let best: {
      lineId: string;
      corner: "tl" | "tr" | "bl" | "br";
      dist: number;
    } | null = null;
    for (const line of projectConstructionLines(this.host.getProject())) {
      if (line.offsetSource) continue;
      if (line.visible === false) continue;
      if (line.id === resizableId) continue;
      const bounds = getUnrotatedBounds(line, scope);
      if (!bounds) continue;
      const corner = this.lineCornerRotationZone(line, bounds, p);
      if (!corner) continue;
      const local = worldToLineLocal(line, p);
      const cx =
        corner === "tl" || corner === "bl" ? bounds.x : bounds.x + bounds.width;
      const cy =
        corner === "tl" || corner === "tr"
          ? bounds.y
          : bounds.y + bounds.height;
      const dx = local.x - cx;
      const dy = local.y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!best || dist < best.dist) {
        best = { lineId: line.id, corner, dist };
      }
    }
    if (!best) return null;
    return { lineId: best.lineId, corner: best.corner };
  }

  private startLineRotationDrag(
    lineId: string,
    p: { x: number; y: number },
    _shiftKey: boolean,
    source: "handle" | "corner",
    pivot?: { x: number; y: number },
  ): void {
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === lineId,
    );
    if (!line) return;
    const scope = this.host.getScope();
    let cx: number;
    let cy: number;
    if (pivot) {
      cx = pivot.x;
      cy = pivot.y;
    } else {
      // Pivot about the user-positioned pivot (issue #420) when set,
      // else the CURRENT displayed bbox centre — not the stored
      // `rotationCenter`, which can drift far from the visible bbox
      // after a chain of compose-rotations. `effectiveLinePivot`
      // resolves both; `composeRotations` in the commit step folds
      // this in to a new equivalent rotation about a derived pivot.
      const eff = effectiveLinePivot(line, scope);
      if (!eff) return;
      cx = eff.x;
      cy = eff.y;
    }
    // Bidirectional editing (issue #180): if the hit line is a
    // mirror, route the rotation through its source root. The user's
    // cursor angle is still measured against the DERIVED pivot they
    // clicked (so the gesture feels natural — the mirror tracks the
    // cursor), but the actual rotation applies to the source via the
    // sign-flipped angle about the inverse-mapped pivot. Mirror
    // re-derives every frame in `Scene.setTransientLineRotation`
    // and ends up tracking the cursor.
    //
    // Refused when the chain hits an offset (no clean inverse
    // through perpendicular-distance offset math). In that case we
    // also refuse the gesture entirely so the user doesn't see a
    // misleading rigid rotation that snap-backs on commit.
    let bidirectional:
      | {
          mirrorId: string;
          derivedPivot: { x: number; y: number };
          sign: 1 | -1;
        }
      | undefined;
    let targetId = lineId;
    let targetCx = cx;
    let targetCy = cy;
    if (isMirrorDerived(line)) {
      const routed = this.tryRouteMirrorRotationToSource(lineId, {
        x: cx,
        y: cy,
      });
      if (!routed) {
        // Mirror-of-offset: refuse the gesture rather than mis-apply
        // a rigid rotation that re-derive would clobber. Brief hint
        // so the user knows the gesture intentionally did nothing.
        showCanvasToast(MIRROR_OFFSET_REFUSE_HINT);
        return;
      }
      bidirectional = {
        mirrorId: lineId,
        derivedPivot: { x: cx, y: cy },
        sign: routed.sign,
      };
      targetId = routed.sourceRootId;
      targetCx = routed.sourcePivot.x;
      targetCy = routed.sourcePivot.y;
    }
    const startAngle = Math.atan2(p.y - cy, p.x - cx);
    this.lineRotationDrag = {
      constructionId: targetId,
      cx: targetCx,
      cy: targetCy,
      startAngle,
      appliedDeg: 0,
      last: { x: p.x, y: p.y },
      source,
      // Anchor-pivot rotations now also track via composition (see
      // `composeRotations` in `model/constructionRotation.ts`) —
      // the new rotation composes with the existing one into a
      // single equivalent rotation about a derived pivot, so the
      // bbox-tilt invariant survives a pivot change.
      trackRotation: true,
      ...(bidirectional ? { bidirectionalMirror: bidirectional } : {}),
    };
    this.host.getScene().setLineRotationHoverCorner(null);
    this.host.getScene().setTransientLineRotation({
      constructionId: targetId,
      cx: targetCx,
      cy: targetCy,
      deg: 0,
    });
    // Drag cursor: classic `grabbing` (closed hand) for the top
    // handle and the rotation-around-the-point anchor — both rotation
    // gestures the user is already gripping — matching their `grab`
    // hover affordance. The corner pivot keeps the 4-way `move`
    // (issue #397).
    setCanvasCursor(scope, source === "corner" ? "move" : "grabbing");
  }

  private updateLineRotationDrag(
    p: { x: number; y: number },
    shiftKey: boolean,
  ): boolean {
    const r = this.lineRotationDrag;
    if (!r) return false;
    // For a bidirectional drag the cursor angle is measured against
    // the DERIVED pivot (where the user clicked on the mirror), not
    // the source-space pivot stored in r.cx/cy. The resulting delta
    // is then sign-flipped per chain parity before driving the
    // source rotation.
    const cursorCx = r.bidirectionalMirror
      ? r.bidirectionalMirror.derivedPivot.x
      : r.cx;
    const cursorCy = r.bidirectionalMirror
      ? r.bidirectionalMirror.derivedPivot.y
      : r.cy;
    const angle = Math.atan2(p.y - cursorCy, p.x - cursorCx);
    let deltaDeg = ((angle - r.startAngle) * 180) / Math.PI;
    while (deltaDeg > 180) deltaDeg -= 360;
    while (deltaDeg < -180) deltaDeg += 360;
    // Step-snap the rotation delta when Shift is held OR the
    // Angle-snap toggle is on. Matches `isAngleSnapActive`'s
    // semantics for the rest of the drawing tools so the toggle
    // acts globally instead of being a draw-only modifier.
    if (isAngleSnapActive(this.host.getSettings(), shiftKey)) {
      deltaDeg =
        Math.round(deltaDeg / RotationController.ROTATION_SHIFT_SNAP_DEG) *
        RotationController.ROTATION_SHIFT_SNAP_DEG;
    }
    // Apply chain-parity sign for bidirectional drags so the
    // source rotates by ∓θ when the user spins the mirror by θ.
    const appliedDeg = r.bidirectionalMirror
      ? r.bidirectionalMirror.sign * deltaDeg
      : deltaDeg;
    r.appliedDeg = appliedDeg;
    r.last = { x: p.x, y: p.y };
    this.host.getScene().setTransientLineRotation({
      constructionId: r.constructionId,
      cx: r.cx,
      cy: r.cy,
      deg: appliedDeg,
      // Bidirectional editing (#180): Scene rotates the mirror's
      // overlay items (bbox + handle the user is interacting with)
      // by the equivalent derived-space rotation so the bbox tilts
      // in lock-step with the cursor — not just on commit.
      ...(r.bidirectionalMirror
        ? { bidirectionalMirror: r.bidirectionalMirror }
        : {}),
    });
    // Mirror the draw-time angle hint chip next to the snap toolbar
    // so the user sees the live rotation amount while spinning.
    // `setAngleHint` takes code-space y-down radians; deltaDeg is in
    // the same convention so the chip's "measurement" arc reads as
    // the rotation amount.
    setAngleHint((deltaDeg * Math.PI) / 180);
    // On-canvas sweep-arc guide (#921) — the chip alone gives a number but no
    // sense of the turn. Measured against the same (possibly derived) pivot the
    // cursor angle uses, so the arc tracks the visible rotation.
    this.showRotationSweep(cursorCx, cursorCy, r.startAngle, deltaDeg, p);
    // Live snap check: after the transient rotation has moved the
    // rendered geometry, read the post-rotation anchor positions off
    // the rotated/partner items and find the nearest other-line
    // anchor within snap radius. Matches the per-frame snap pipeline
    // used by vertex / line drag, so the highlight ring + on-release
    // pull behave the same way.
    this.refreshRotationLiveSnap(new Set([r.constructionId]));
    return true;
  }

  /** Draw the rotation sweep-arc guide (#921) — built by `rotationSweepRays`,
   *  which leather's TransformController draws from too (#990), so the two
   *  rotation gestures can't drift apart. `null` (a turn too small to draw)
   *  clears, so a just-started drag doesn't flash a stray radial stub. */
  private showRotationSweep(
    pivotX: number,
    pivotY: number,
    startAngle: number,
    deltaDeg: number,
    ref: { x: number; y: number },
  ): void {
    this.host
      .getScene()
      .setGuideRays(
        rotationSweepRays(
          { x: pivotX, y: pivotY },
          startAngle,
          deltaDeg,
          ref,
          this.zoom(),
        ),
      );
  }

  /** Per-frame rotation-drag snap check. Reads post-rotation anchor
   *  positions off the rotated + partner rendered items, runs
   *  `findNearestSnapCandidate`, and updates the shared `liveSnap` +
   *  the on-canvas hover ring. Skipped when the snap-point toggle is
   *  off so the rotation gesture matches the rest of the snap UX. */
  private refreshRotationLiveSnap(rotatedIds: ReadonlySet<string>): void {
    if (!isPointSnapActive(this.host.getSettings())) {
      this.host.clearLiveSnap();
      return;
    }
    const scope = this.host.getScope();
    const project = this.host.getProject();
    const sources: Array<{
      ref: SnapRefLite;
      pos: { x: number; y: number };
    }> = [];
    const excluded = new Set<string>(rotatedIds);
    // Source = every anchor on the rotated lines, read from the live
    // rendered item (paper.js mutates segments in place when `.rotate`
    // runs in `rotateRenderedLineItems`).
    for (const lineId of rotatedIds) {
      const item = this.host.getScene().getConstructionLineItem(lineId);
      if (!item) continue;
      const cp = item as paper.CompoundPath;
      const subs: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [item as unknown as paper.Path];
      subs.forEach((sub, pathIndex) => {
        sub.segments.forEach((seg, segmentIndex) => {
          sources.push({
            ref: {
              kind: "anchor",
              constructionId: lineId,
              pathIndex,
              segmentIndex,
            },
            pos: { x: seg.point.x, y: seg.point.y },
          });
        });
      });
    }
    // Partner lines (snap-chain partners + offset descendants) also
    // translate during the rotation — read their live positions too
    // so partner anchors get checked for snap candidates. Their ids
    // need to be excluded from the candidate pool (they're moving).
    for (const line of projectConstructionLines(project)) {
      if (rotatedIds.has(line.id)) continue;
      const item = this.host.getScene().getConstructionLineItem(line.id);
      if (!item) continue;
      // Heuristic: a line whose live rendered bounds differ from its
      // model pathData bounds is currently being moved by the
      // transient rotation pass — treat it as a partner. Cheaper than
      // re-running `rotationPartnerTranslations` per frame.
      const liveBounds = item.bounds;
      let modelMinX = Number.POSITIVE_INFINITY;
      let modelMinY = Number.POSITIVE_INFINITY;
      try {
        const cp = new scope.CompoundPath({
          pathData: line.pathData,
          insert: false,
        });
        modelMinX = cp.bounds.x;
        modelMinY = cp.bounds.y;
        cp.remove();
      } catch {
        continue;
      }
      if (
        Math.hypot(modelMinX - liveBounds.x, modelMinY - liveBounds.y) < 0.001
      ) {
        // Stationary — keep it as a snap-target candidate, not a
        // source.
        continue;
      }
      excluded.add(line.id);
      const cp = item as paper.CompoundPath;
      const subs: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [item as unknown as paper.Path];
      subs.forEach((sub, pathIndex) => {
        sub.segments.forEach((seg, segmentIndex) => {
          sources.push({
            ref: {
              kind: "anchor",
              constructionId: line.id,
              pathIndex,
              segmentIndex,
            },
            pos: { x: seg.point.x, y: seg.point.y },
          });
        });
      });
    }
    if (sources.length === 0) {
      this.host.clearLiveSnap();
      return;
    }
    const hit = findNearestSnapCandidate(
      projectConstructionLines(project),
      sources,
      this.host.snapRadiusMm(),
      scope,
      excluded,
    );
    if (!hit) {
      this.host.clearLiveSnap();
      return;
    }
    this.host.setLiveSnap({
      source: hit.sourceRef,
      candidate: hit.candidateRef,
      candidatePos: hit.candidatePos,
    });
    this.host.refreshLiveSnapOverlay();
  }

  /** Per-frame update of a tracing-image rotation drag (issue #526):
   *  spin the image to `startDeg + (pointerAngle − startPointerAngle)`,
   *  snapping to 15° increments while Shift is held. Live only — the
   *  raster + rotation box follow via `setTracingImageRotationLive`;
   *  the model commits on release. */
  private updateImageRotationDrag(
    p: { x: number; y: number },
    shiftKey: boolean,
  ): void {
    const drag = this.imageRotationDrag;
    if (!drag) return;
    const angle = Math.atan2(p.y - drag.cy, p.x - drag.cx);
    const deltaDeg = ((angle - drag.startPointerAngle) * 180) / Math.PI;
    let target = drag.startDeg + deltaDeg;
    if (shiftKey) target = Math.round(target / 15) * 15;
    drag.lastDeg = target;
    // Live-orbit about the drag's pivot (centre when no custom pivot set).
    this.host.getScene().setTracingImageRotationLive(drag.id, target, {
      x: drag.cx,
      y: drag.cy,
    });
  }

  private commitImageRotationDrag(): void {
    const drag = this.imageRotationDrag;
    this.imageRotationDrag = null;
    if (!drag) return;
    // Normalise to (-180, 180] so the stored value stays canonical.
    let n = drag.lastDeg % 360;
    if (n > 180) n -= 360;
    if (n <= -180) n += 360;
    if (Math.abs(n - drag.startDeg) < 0.01) {
      // No meaningful change — re-render so the live tracking resets.
      this.host.getScene().render(this.host.getProject());
      return;
    }
    const id = drag.id;
    const netDeg = n - drag.startDeg;
    const px = drag.cx;
    const py = drag.cy;
    // Apply the net turn about the pivot via the shared transform — orbits
    // the image when the pivot is off-centre, or just spins it in place when
    // the pivot is the centre (same result as setting `rotation`).
    this.host.commit("Rotate tracing image", (proj) => ({
      ...proj,
      tracingImages: projectTracingImages(proj).map((img) =>
        img.id === id ? rotateTracingImage(img, netDeg, px, py) : img,
      ),
    }));
  }

  private commitLineRotationDrag(): void {
    const r = this.lineRotationDrag;
    this.lineRotationDrag = null;
    // Capture the per-frame snap target before we tear down the
    // overlay — the commit reducer needs it to translate the
    // rotated set onto the candidate and (if the constraint toggle
    // is on) record the pair.
    const snapAtRelease = this.host.getLiveSnap();
    this.host.clearLiveSnap();
    this.host.getScene().setTransientLineRotation(null);
    setAngleHint(null);
    this.host.getScene().setGuideRays(null);
    setCanvasCursor(this.host.getScope(), "");
    if (!r) return;
    if (Math.abs(r.appliedDeg) < 0.01) return;
    const scope = this.host.getScope();
    const lineId = r.constructionId;
    const cx = r.cx;
    const cy = r.cy;
    const deg = r.appliedDeg;
    const trackRotation = r.trackRotation;
    const rotatedSet = new Set([lineId]);
    const settings = this.host.getSettings();
    this.host.commit("Rotate construction line", (proj) => {
      const partners = rotationPartnerTranslations(
        proj,
        rotatedSet,
        deg,
        cx,
        cy,
        scope,
      );
      const translated = projectConstructionLines(proj).map((l) => {
        if (l.id === lineId) {
          return rotateConstructionLine(l, deg, cx, cy, scope, {
            trackRotation,
          });
        }
        const t = partners.get(l.id);
        if (!t) return l;
        return translateLineForRotationPartner(l, t.dx, t.dy, scope);
      });
      const stretched = this.stretchRotationPartners(
        proj,
        translated,
        rotatedSet,
      );
      const rotatedLines = stretched.constructionLines ?? translated;
      // Snap-on-release: use the per-frame snap pair captured during
      // the drag (highlighted via the shared `liveSnap`). Snap toggle
      // `snapPoint` gates the geometry pull onto the candidate;
      // `snapPointConstraint` separately gates whether a coincident
      // constraint is also recorded. Matches the per-point / whole-
      // line drag flows.
      const movingIds = new Set<string>([lineId, ...partners.keys()]);
      const { lines: snappedLines, newConstraint } =
        this.applyCapturedRotationSnap(
          proj,
          rotatedLines,
          movingIds,
          snapAtRelease,
          settings,
          scope,
        );
      const next = {
        ...proj,
        constructionLines: snappedLines,
        constraints: stretched.constraints,
      };
      if (!newConstraint) return next;
      return {
        ...next,
        constraints: [...(next.constraints ?? []), newConstraint],
      };
    });
  }

  /** Partners linked to the rotated line(s) at TWO points can't follow by
   *  the translations `rotationPartnerTranslations` hands out — one link
   *  would break free. Run snap enforcement over the rotated-and-translated
   *  geometry with the rotated lines pinned, so such a partner stretches and
   *  both links stay attached (#1166), then settle any partner whose shape
   *  changed (a stretched rectangle stops claiming to be one). Rigid
   *  partners come back untouched. The live drag shows the same result
   *  (`TransientTransformController.applyTransientRotationStretch`). */
  private stretchRotationPartners(
    proj: Project,
    translated: ConstructionLine[],
    rotatedIds: ReadonlySet<string>,
  ): Project {
    return withSnappedPartnersFollowing(
      proj,
      translated,
      rotatedIds,
      this.host.getScope(),
    );
  }

  /** Rotation snap-on-release adapter. Reads the per-frame snap pair
   *  captured in the shared `liveSnap` (set by `refreshRotationLiveSnap`)
   *  and applies it to the just-committed post-rotation geometry.
   *
   *  Two-stage gating, matching the rest of the snap UX:
   *    - `isPointSnapActive` (snap toggle): translates the moving
   *      set so the source/candidate pair lands exactly coincident.
   *    - `isPointConstraintActive` (constraint toggle): additionally
   *      records a new coincident constraint pinning the pair.
   *  When the snap toggle is off, no translation runs even if the
   *  per-frame check fired — the user opted out of the snap pull. */
  private applyCapturedRotationSnap(
    proj: Project,
    postRotationLines: ConstructionLine[],
    movingLineIds: ReadonlySet<string>,
    snap: {
      source: SnapRefLite;
      candidate: SnapRefLite;
      candidatePos: { x: number; y: number };
    } | null,
    settings: ToolSettings,
    scope: paper.PaperScope,
  ): {
    lines: ConstructionLine[];
    newConstraint: Constraint | null;
    delta: { dx: number; dy: number } | null;
  } {
    if (!snap || !isPointSnapActive(settings)) {
      return {
        lines: postRotationLines,
        newConstraint: null,
        delta: null,
      };
    }
    if (snap.source.kind !== "anchor") {
      return {
        lines: postRotationLines,
        newConstraint: null,
        delta: null,
      };
    }
    // Look up the source anchor's *current* (post-rotation) position
    // off the line list so the translation lines up with the
    // committed geometry, not the stale per-frame value (the model
    // hadn't been updated when `liveSnap` was captured).
    let cp: paper.CompoundPath | null = null;
    let srcPos: { x: number; y: number } | null = null;
    const sourceRef = snap.source as AnchorRefLite;
    const sourceLine = postRotationLines.find(
      (l) => l.id === sourceRef.constructionId,
    );
    if (sourceLine) {
      try {
        cp = new scope.CompoundPath({
          pathData: sourceLine.pathData,
          insert: false,
        });
        const subs: paper.Path[] =
          cp.children && cp.children.length > 0
            ? (cp.children as paper.Path[])
            : [cp as unknown as paper.Path];
        const sub = subs[sourceRef.pathIndex];
        const seg = sub?.segments[sourceRef.segmentIndex];
        if (seg) srcPos = { x: seg.point.x, y: seg.point.y };
        cp.remove();
      } catch {
        srcPos = null;
      }
    }
    if (!srcPos) {
      return {
        lines: postRotationLines,
        newConstraint: null,
        delta: null,
      };
    }
    const dx = snap.candidatePos.x - srcPos.x;
    const dy = snap.candidatePos.y - srcPos.y;
    const adjustedLines =
      Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6
        ? postRotationLines
        : postRotationLines.map((l) =>
            movingLineIds.has(l.id)
              ? translateLineForRotationPartner(l, dx, dy, scope)
              : l,
          );
    // Constraint creation is a separate opt-in. Also dedupe so a
    // repeat rotation that lands on the same pair doesn't stack.
    let newConstraint: Constraint | null = null;
    if (
      isPointConstraintActive(settings) &&
      isPinnableSnapRef(snap.candidate)
    ) {
      const searchProj: Project = {
        ...proj,
        constructionLines: adjustedLines,
      };
      if (!hasCoincidentConstraint(searchProj, snap.source, snap.candidate)) {
        newConstraint = {
          id: newId(),
          kind: "coincident",
          refs: [
            {
              kind: "anchor",
              constructionId: sourceRef.constructionId,
              pathIndex: sourceRef.pathIndex,
              segmentIndex: sourceRef.segmentIndex,
            },
            snapRefToConstraintRef(snap.candidate),
          ],
        };
      }
    }
    return { lines: adjustedLines, newConstraint, delta: { dx, dy } };
  }

  private cancelLineRotationDrag(): void {
    if (!this.lineRotationDrag) return;
    this.lineRotationDrag = null;
    this.host.getScene().setTransientLineRotation(null);
    setAngleHint(null);
    this.host.getScene().setGuideRays(null);
    this.host.clearLiveSnap();
    setCanvasCursor(this.host.getScope(), "");
    this.host.rerender();
  }

  /** Update the rotation hover state + cursor while the cursor moves
   *  over the canvas in select sub-mode. Detects (in priority order):
   *  the selected line's top handle, any visible line's bbox corner
   *  zone, any visible line's arbitrary anchor. No-op during any
   *  existing drag (the drag owns the cursor / overlay). */
  private updateLineRotationHover(p: { x: number; y: number }): void {
    if (
      this.lineRotationDrag ||
      this.host.isLineDragActive() ||
      this.host.isGroupDragActive() ||
      this.host.isLassoActive()
    ) {
      return;
    }
    const scope = this.host.getScope();
    // Corner resize handles on the selected line win first — they sit
    // at the bbox corners and replace the corner rotation gesture there
    // (issue #388).
    const resizeHit = this.host.resizeHandleAt(p);
    if (resizeHit) {
      this.host.getScene().setLineRotationHoverCorner(null);
      setCanvasCursor(scope, this.host.resizeCursorFor(resizeHit.handle));
      return;
    }
    // Top handle on the currently-selected line wins next — it's
    // the only gesture that's anchored to selection state.
    const selectedId = this.host.rotatableSelectedLineId();
    if (selectedId) {
      const selectedLine = projectConstructionLines(
        this.host.getProject(),
      ).find((l) => l.id === selectedId);
      if (selectedLine) {
        const bounds = getUnrotatedBounds(selectedLine, scope);
        if (bounds && this.isOnLineRotationHandle(selectedLine, bounds, p)) {
          this.host.getScene().setLineRotationHoverCorner(null);
          setCanvasCursor(scope, "grab");
          return;
        }
      }
    }
    // Corner rotation removed in #388 (corners resize the selected
    // line instead — handled above). Anchor-pivot rotation replaced
    // by the draggable pivot crosshair (issue #420), whose hover is
    // handled in onHover before this method runs.
    this.host.getScene().setLineRotationHoverCorner(null);
  }

  // -------------------------------------------------------------------
  // Pivot snap
  // -------------------------------------------------------------------

  /** Resolve a snap target for the dragging rotation pivot (issue
   *  #420). The bbox-centre candidate (`centerCandidate`) is always
   *  considered — that's the "home" position the user snaps back to
   *  to clear a custom pivot. Anchors of every visible non-offset
   *  line are considered too, gated behind the point-snap toggle.
   *  Returns the closest candidate within `PIVOT_SNAP_RADIUS_MM`, or
   *  null when nothing is close enough (free-floating drag). */
  private nearestPivotSnap(
    p: { x: number; y: number },
    centerCandidate: { x: number; y: number } | null,
  ): { x: number; y: number } | null {
    const scope = this.host.getScope();
    // A finger drops it from a little farther away (#1150).
    const radiusSq =
      snapRadiusFor(
        RotationController.PIVOT_SNAP_RADIUS_MM,
        this.pointerKind,
      ) ** 2;
    const candidates: { x: number; y: number }[] = [];
    if (centerCandidate) candidates.push(centerCandidate);
    if (isPointSnapActive(this.host.getSettings())) {
      for (const line of projectConstructionLines(this.host.getProject())) {
        if (line.offsetSource) continue;
        if (line.visible === false) continue;
        let path: paper.PathItem;
        try {
          path = new scope.CompoundPath({
            pathData: line.pathData,
            insert: false,
          });
        } catch {
          continue;
        }
        const subs: paper.Path[] =
          path.children && path.children.length > 0
            ? (path.children as paper.Path[])
            : [path as unknown as paper.Path];
        for (const sub of subs) {
          for (const seg of sub.segments) {
            candidates.push({ x: seg.point.x, y: seg.point.y });
          }
        }
      }
    }
    let best: { x: number; y: number } | null = null;
    let bestSq = radiusSq;
    for (const c of candidates) {
      const dx = p.x - c.x;
      const dy = p.y - c.y;
      const dSq = dx * dx + dy * dy;
      if (dSq <= bestSq) {
        best = c;
        bestSq = dSq;
      }
    }
    return best ? { x: best.x, y: best.y } : null;
  }

  // -------------------------------------------------------------------
  // Group rotation gesture
  // -------------------------------------------------------------------

  /** Returns the id of the single selected construction group (no
   *  other competing selections), or null. */
  private singleSelectedGroupId(): string | null {
    return this.host.getScene().singleSelectedConstructionGroupId();
  }

  /** Project-space un-rotated bbox of one construction group, plus
   *  its rotation pivot. Matches the geometry the Scene uses to
   *  draw the group overlay so the hit-test rings hug the rendered
   *  tilted bbox rather than a fresh axis-aligned one (issue #67). */
  private getGroupUnrotatedBox(groupId: string): {
    x: number;
    y: number;
    width: number;
    height: number;
    cx: number;
    cy: number;
    rotation: number;
  } | null {
    const proj = this.host.getProject();
    const lineIds = descendantConstructionLineIds(proj, groupId);
    const imageIds = descendantConstructionImageIds(proj, groupId);
    if (lineIds.length === 0 && imageIds.length === 0) return null;
    const group = (proj.constructionGroups ?? []).find((g) => g.id === groupId);
    if (group) {
      const b = getUnrotatedGroupBounds(proj, group, this.host.getScope());
      if (b) return { ...b, rotation: group.rotation ?? 0 };
    }
    // Fallback: legacy group without rotation metadata — read live
    // rendered line bounds unioned with member image rects (#545).
    let union: paper.Rectangle | null = null;
    for (const lid of lineIds) {
      const item = this.host.getScene().getConstructionLineItem(lid);
      if (!item) continue;
      const b = item.bounds;
      union = union ? union.unite(b) : b;
    }
    if (imageIds.length > 0) {
      const imageSet = new Set(imageIds);
      for (const img of projectTracingImages(proj)) {
        if (!imageSet.has(img.id)) continue;
        const r = new (this.host.getScope().Rectangle)(
          img.x,
          img.y,
          img.widthMm,
          img.heightMm,
        );
        union = union ? union.unite(r) : r;
      }
    }
    if (!union) return null;
    return {
      x: union.x,
      y: union.y,
      width: union.width,
      height: union.height,
      cx: union.x + union.width / 2,
      cy: union.y + union.height / 2,
      rotation: 0,
    };
  }

  /** Map a world-space point into the group's un-rotated reference
   *  frame so axis-aligned bbox checks just work. Inverse of "render
   *  the group bbox tilted by `group.rotation` about the pivot". */
  private worldToGroupLocal(
    box: { cx: number; cy: number; rotation: number },
    p: { x: number; y: number },
  ): { x: number; y: number } {
    const rot = box.rotation;
    if (Math.abs(rot) < 1e-6) return { x: p.x, y: p.y };
    const rad = (-rot * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const dx = p.x - box.cx;
    const dy = p.y - box.cy;
    return { x: box.cx + dx * c - dy * s, y: box.cy + dx * s + dy * c };
  }

  private isOnGroupRotationHandle(
    box: {
      x: number;
      y: number;
      width: number;
      height: number;
      cx: number;
      cy: number;
      rotation: number;
    },
    p: { x: number; y: number },
  ): boolean {
    // Screen-constant knob position + hit radius (both / zoom), from the
    // shared geometry (#990).
    return isOnRotationKnob(box, this.worldToGroupLocal(box, p), {
      zoom: this.zoom(),
      radiusMm: hitRadiusFor(
        RotationController.ROTATION_HANDLE_HIT_RADIUS_MM,
        this.pointerKind,
      ),
    });
  }

  private groupCornerRotationZone(
    box: {
      x: number;
      y: number;
      width: number;
      height: number;
      cx: number;
      cy: number;
      rotation: number;
    },
    p: { x: number; y: number },
  ): "tl" | "tr" | "bl" | "br" | null {
    const local = this.worldToGroupLocal(box, p);
    const zoom = this.zoom();
    const centerX = box.x + box.width / 2;
    const centerY = box.y + box.height / 2;
    const corners: {
      key: "tl" | "tr" | "bl" | "br";
      x: number;
      y: number;
    }[] = [
      { key: "tl", x: box.x, y: box.y },
      { key: "tr", x: box.x + box.width, y: box.y },
      { key: "bl", x: box.x, y: box.y + box.height },
      { key: "br", x: box.x + box.width, y: box.y + box.height },
    ];
    for (const c of corners) {
      const dx = local.x - c.x;
      const dy = local.y - c.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (
        dist < RotationController.ROTATION_CORNER_INNER_MM / zoom ||
        dist >
          hitRadiusFor(
            RotationController.ROTATION_CORNER_OUTER_MM,
            this.pointerKind,
          ) /
            zoom
      ) {
        continue;
      }
      const outsideX =
        c.x === centerX ? false : (local.x - c.x) * (c.x - centerX) > 0;
      const outsideY =
        c.y === centerY ? false : (local.y - c.y) * (c.y - centerY) > 0;
      if (outsideX && outsideY) return c.key;
    }
    return null;
  }

  maybeStartGroupRotationDrag(
    p: { x: number; y: number },
    shiftKey: boolean,
  ): boolean {
    const groupId = this.singleSelectedGroupId();
    if (!groupId) return false;
    const box = this.getGroupUnrotatedBox(groupId);
    if (!box) return false;
    if (this.isOnGroupRotationHandle(box, p)) {
      this.startGroupRotationDrag(groupId, p, shiftKey, "handle");
      return true;
    }
    const corner = this.groupCornerRotationZone(box, p);
    if (corner) {
      this.startGroupRotationDrag(groupId, p, shiftKey, "corner");
      return true;
    }
    return false;
  }

  private startGroupRotationDrag(
    groupId: string,
    p: { x: number; y: number },
    _shiftKey: boolean,
    source: "handle" | "corner",
  ): void {
    const box = this.getGroupUnrotatedBox(groupId);
    if (!box) return;
    // Pivot about the user-positioned pivot (issue #420) when set,
    // else the CURRENT displayed bbox centre (the un-rotated bbox
    // centre rotated by `box.rotation` about the stored pivot) —
    // not the raw stored pivot itself, which keeps the bbox-center
    // gesture intuitive after a chain of compose-rotations.
    const group = (this.host.getProject().constructionGroups ?? []).find(
      (g) => g.id === groupId,
    );
    const eff = group?.pivotPoint ?? displayedBboxCenter(box, box.rotation);
    const cx = eff.x;
    const cy = eff.y;
    const startAngle = Math.atan2(p.y - cy, p.x - cx);
    this.groupRotationDrag = {
      constructionGroupId: groupId,
      cx,
      cy,
      startAngle,
      appliedDeg: 0,
      last: { x: p.x, y: p.y },
      source,
    };
    this.host.getScene().setGroupRotationHoverCorner(null);
    this.host.getScene().setTransientGroupRotation({
      constructionGroupId: groupId,
      cx,
      cy,
      deg: 0,
    });
    setCanvasCursor(
      this.host.getScope(),
      source === "handle" ? "grabbing" : "move",
    );
  }

  private updateGroupRotationDrag(
    p: { x: number; y: number },
    shiftKey: boolean,
  ): boolean {
    const r = this.groupRotationDrag;
    if (!r) return false;
    const angle = Math.atan2(p.y - r.cy, p.x - r.cx);
    let deltaDeg = ((angle - r.startAngle) * 180) / Math.PI;
    while (deltaDeg > 180) deltaDeg -= 360;
    while (deltaDeg < -180) deltaDeg += 360;
    // Same Shift / Angle-snap step as the per-line rotation update.
    if (isAngleSnapActive(this.host.getSettings(), shiftKey)) {
      deltaDeg =
        Math.round(deltaDeg / RotationController.ROTATION_SHIFT_SNAP_DEG) *
        RotationController.ROTATION_SHIFT_SNAP_DEG;
    }
    r.appliedDeg = deltaDeg;
    r.last = { x: p.x, y: p.y };
    this.host.getScene().setTransientGroupRotation({
      constructionGroupId: r.constructionGroupId,
      cx: r.cx,
      cy: r.cy,
      deg: deltaDeg,
    });
    setAngleHint((deltaDeg * Math.PI) / 180);
    this.showRotationSweep(r.cx, r.cy, r.startAngle, deltaDeg, p);
    const memberIds = new Set(
      descendantConstructionLineIds(
        this.host.getProject(),
        r.constructionGroupId,
      ),
    );
    this.refreshRotationLiveSnap(memberIds);
    return true;
  }

  private commitGroupRotationDrag(): void {
    const r = this.groupRotationDrag;
    this.groupRotationDrag = null;
    const snapAtRelease = this.host.getLiveSnap();
    this.host.clearLiveSnap();
    this.host.getScene().setTransientGroupRotation(null);
    setAngleHint(null);
    this.host.getScene().setGuideRays(null);
    setCanvasCursor(this.host.getScope(), "");
    if (!r) return;
    if (Math.abs(r.appliedDeg) < 0.01) return;
    const scope = this.host.getScope();
    const groupId = r.constructionGroupId;
    const cx = r.cx;
    const cy = r.cy;
    const deg = r.appliedDeg;
    const memberLineIds = new Set(
      descendantConstructionLineIds(this.host.getProject(), groupId),
    );
    const memberImageIds = new Set(
      descendantConstructionImageIds(this.host.getProject(), groupId),
    );
    const settings = this.host.getSettings();
    this.host.commit("Rotate group", (proj) => {
      const partners = rotationPartnerTranslations(
        proj,
        memberLineIds,
        deg,
        cx,
        cy,
        scope,
      );
      const groups = (proj.constructionGroups ?? []).map((g) => {
        if (g.id !== groupId) return g;
        const oldDeg = g.rotation ?? 0;
        const oldCenter = g.rotationCenter;
        // Compose with the existing group rotation so a rotation
        // about the displayed bbox centre (which can differ from
        // the stored pivot after a chain of compose-rotations)
        // still produces a coherent (rotation, rotationCenter)
        // pair. Falls back to clearing the field when the sum
        // collapses to a pure translation.
        if (oldCenter && Math.abs(oldDeg) > 1e-6) {
          const composed = composeRotations(oldDeg, oldCenter, deg, {
            x: cx,
            y: cy,
          });
          if (composed) {
            return {
              ...g,
              rotation: composed.deg,
              rotationCenter: composed.center,
            };
          }
          const stripped = { ...g };
          delete stripped.rotation;
          delete stripped.rotationCenter;
          return stripped;
        }
        return {
          ...g,
          rotation: normalizeRotation(deg),
          rotationCenter: { x: cx, y: cy },
        };
      });
      const translated = projectConstructionLines(proj).map((l) => {
        if (memberLineIds.has(l.id)) {
          return rotateConstructionLine(l, deg, cx, cy, scope, {
            trackRotation: false,
          });
        }
        const t = partners.get(l.id);
        if (!t) return l;
        return translateLineForRotationPartner(l, t.dx, t.dy, scope);
      });
      const stretched = this.stretchRotationPartners(
        proj,
        translated,
        memberLineIds,
      );
      const rotatedLines = stretched.constructionLines ?? translated;
      // Snap-on-release: same path as the single-line rotation
      // commit, but the moving set is every member of the rotated
      // group plus every partner translated by the partners map.
      // The group's `rotationCenter` is also shifted by the snap
      // delta below so the recorded pivot stays consistent with
      // the geometry after the post-rotation translate.
      const movingIds = new Set<string>([...memberLineIds, ...partners.keys()]);
      const {
        lines: snappedLines,
        newConstraint,
        delta: snapDelta,
      } = this.applyCapturedRotationSnap(
        proj,
        rotatedLines,
        movingIds,
        snapAtRelease,
        settings,
        scope,
      );
      // When a snap fires we also shift the group's recorded
      // rotationCenter AND user pivot (issue #420) by the same delta —
      // otherwise the next rotation gesture would pivot around a
      // stale point.
      const groupsAdjusted = snapDelta
        ? groups.map((g) =>
            g.id === groupId && (g.rotationCenter || g.pivotPoint)
              ? {
                  ...g,
                  rotationCenter: g.rotationCenter
                    ? {
                        x: g.rotationCenter.x + snapDelta.dx,
                        y: g.rotationCenter.y + snapDelta.dy,
                      }
                    : undefined,
                  pivotPoint: g.pivotPoint
                    ? {
                        x: g.pivotPoint.x + snapDelta.dx,
                        y: g.pivotPoint.y + snapDelta.dy,
                      }
                    : undefined,
                }
              : g,
          )
        : groups;
      // Member tracing images (issue #545) rotate rigidly with the group:
      // rotateTracingImage orbits each about the pivot and spins it about
      // its own centre by `deg`. When a snap fired the whole cluster shifts
      // by the same delta, so images ride that translation too.
      const next: Project = {
        ...proj,
        constructionGroups: groupsAdjusted,
        constructionLines: snappedLines,
        constraints: stretched.constraints,
      };
      if (memberImageIds.size > 0) {
        next.tracingImages = projectTracingImages(proj).map((img) => {
          if (!memberImageIds.has(img.id)) return img;
          const rotated = rotateTracingImage(img, deg, cx, cy);
          if (!snapDelta) return rotated;
          return {
            ...rotated,
            x: rotated.x + snapDelta.dx,
            y: rotated.y + snapDelta.dy,
          };
        });
      }
      if (!newConstraint) return next;
      return {
        ...next,
        constraints: [...(next.constraints ?? []), newConstraint],
      };
    });
  }

  private cancelGroupRotationDrag(): void {
    if (!this.groupRotationDrag) return;
    this.groupRotationDrag = null;
    this.host.getScene().setTransientGroupRotation(null);
    setAngleHint(null);
    this.host.getScene().setGuideRays(null);
    this.host.clearLiveSnap();
    setCanvasCursor(this.host.getScope(), "");
    this.host.rerender();
  }

  private updateGroupRotationHover(p: { x: number; y: number }): void {
    if (
      this.groupRotationDrag ||
      this.lineRotationDrag ||
      this.host.isLineDragActive() ||
      this.host.isGroupDragActive() ||
      this.host.isLassoActive()
    ) {
      return;
    }
    const groupId = this.singleSelectedGroupId();
    if (!groupId) {
      this.host.getScene().setGroupRotationHoverCorner(null);
      return;
    }
    const box = this.getGroupUnrotatedBox(groupId);
    if (!box) {
      this.host.getScene().setGroupRotationHoverCorner(null);
      return;
    }
    const scope = this.host.getScope();
    if (this.isOnGroupRotationHandle(box, p)) {
      this.host.getScene().setGroupRotationHoverCorner(null);
      setCanvasCursor(scope, "grab");
      return;
    }
    const corner = this.groupCornerRotationZone(box, p);
    if (corner) {
      this.host.getScene().setGroupRotationHoverCorner({
        constructionGroupId: groupId,
        corner,
      });
      setCanvasCursor(scope, "move");
      return;
    }
    this.host.getScene().setGroupRotationHoverCorner(null);
  }

  // -------------------------------------------------------------------
  // Pivot drag gesture (issue #420)
  // -------------------------------------------------------------------

  /** Hit-test the always-on rotation pivot crosshair. Returns the
   *  draggable owner (the selected line / group) when `p` is within
   *  `PIVOT_HIT_RADIUS_MM` of its effective pivot, else null. The
   *  pivot and crosshair live in world space, so a plain Euclidean
   *  distance check is correct (no tilted-frame mapping needed).
   *  Derived (offset / mirror) lines are excluded — their pivot is
   *  shown read-only and repositioned by editing the source. */
  private pivotHandleAt(p: {
    x: number;
    y: number;
  }):
    | { kind: "line"; lineId: string }
    | { kind: "group"; groupId: string }
    | { kind: "image"; imageId: string }
    | null {
    const scope = this.host.getScope();
    // Screen-constant grab zone (#864): the crosshair is drawn at a fixed
    // on-screen size, so its hit radius divides by the live zoom too.
    // A finger grabs it wider (#1150); the crosshair is drawn the same size.
    const rSq =
      (hitRadiusFor(RotationController.PIVOT_HIT_RADIUS_MM, this.pointerKind) /
        this.zoom()) **
      2;
    const near = (pt: { x: number; y: number } | null): boolean => {
      if (!pt) return false;
      const dx = p.x - pt.x;
      const dy = p.y - pt.y;
      return dx * dx + dy * dy <= rSq;
    };
    const imageId = this.rotatableSelectedImageId();
    if (imageId) {
      const image = projectTracingImages(this.host.getProject()).find(
        (img) => img.id === imageId,
      );
      if (image && near(effectiveImagePivot(image))) {
        return { kind: "image", imageId };
      }
    }
    const lineId = this.host.rotatableSelectedLineId();
    if (lineId) {
      const line = projectConstructionLines(this.host.getProject()).find(
        (l) => l.id === lineId,
      );
      if (line && !isMirrorDerived(line)) {
        if (near(effectiveLinePivot(line, scope))) {
          return { kind: "line", lineId };
        }
      }
    }
    const groupId = this.singleSelectedGroupId();
    if (groupId) {
      const group = (this.host.getProject().constructionGroups ?? []).find(
        (g) => g.id === groupId,
      );
      if (
        group &&
        near(effectiveGroupPivot(this.host.getProject(), group, scope))
      ) {
        return { kind: "group", groupId };
      }
    }
    return null;
  }

  /** Bbox-centre candidate the pivot snaps back to (clears the custom
   *  pivot). Computed by ignoring any stored `pivotPoint` so it's the
   *  natural "home" position even when a custom pivot is set. */
  private pivotHomeFor(
    owner: NonNullable<RotationController["pivotDrag"]>["owner"],
  ): { x: number; y: number } | null {
    const scope = this.host.getScope();
    if (owner.kind === "line") {
      const line = projectConstructionLines(this.host.getProject()).find(
        (l) => l.id === owner.lineId,
      );
      if (!line) return null;
      const bounds = getUnrotatedBounds(line, scope);
      if (!bounds) return null;
      return displayedBboxCenter(bounds, line.rotation ?? 0);
    }
    if (owner.kind === "image") {
      const image = projectTracingImages(this.host.getProject()).find(
        (img) => img.id === owner.imageId,
      );
      // The image's "home" pivot is its own centre — the reset target.
      return image ? tracingImageCenter(image) : null;
    }
    const box = this.getGroupUnrotatedBox(owner.groupId);
    if (!box) return null;
    return displayedBboxCenter(box, box.rotation);
  }

  /** Tell the controller what is pointing, before the hit tests run (#40). */
  setPointerKind(kind: PointerKind) {
    this.pointerKind = kind;
  }

  maybeStartPivotDrag(p: { x: number; y: number }): boolean {
    const hit = this.pivotHandleAt(p);
    if (!hit) return false;
    const scope = this.host.getScope();
    let pivot: { x: number; y: number } | null = null;
    if (hit.kind === "line") {
      const line = projectConstructionLines(this.host.getProject()).find(
        (l) => l.id === hit.lineId,
      );
      pivot = line ? effectiveLinePivot(line, scope) : null;
    } else if (hit.kind === "image") {
      const image = projectTracingImages(this.host.getProject()).find(
        (img) => img.id === hit.imageId,
      );
      pivot = image ? effectiveImagePivot(image) : null;
    } else {
      const group = (this.host.getProject().constructionGroups ?? []).find(
        (g) => g.id === hit.groupId,
      );
      pivot = group
        ? effectiveGroupPivot(this.host.getProject(), group, scope)
        : null;
    }
    if (!pivot) return false;
    this.pivotDrag = {
      owner: hit,
      start: { x: pivot.x, y: pivot.y },
      pivot: { x: pivot.x, y: pivot.y },
      snapped: false,
    };
    this.host.getScene().setTransientPivot(pivot);
    setCanvasCursor(scope, "grabbing");
    return true;
  }

  private updatePivotDrag(
    p: { x: number; y: number },
    _shiftKey: boolean,
  ): void {
    const drag = this.pivotDrag;
    if (!drag) return;
    const home = this.pivotHomeFor(drag.owner);
    const snap = this.nearestPivotSnap(p, home);
    const next = snap ?? { x: p.x, y: p.y };
    drag.pivot = next;
    drag.snapped = snap != null;
    this.host.getScene().setTransientPivot(next);
  }

  private commitPivotDrag(): void {
    const drag = this.pivotDrag;
    this.pivotDrag = null;
    this.host.getScene().setTransientPivot(null);
    setCanvasCursor(this.host.getScope(), "");
    if (!drag) return;
    // No-op guard: a press that didn't move the pivot meaningfully
    // shouldn't push a history entry.
    const moved =
      Math.hypot(drag.pivot.x - drag.start.x, drag.pivot.y - drag.start.y) >
      0.01;
    if (!moved) {
      this.host.rerender();
      return;
    }
    // Snapping the pivot back onto the displayed bbox centre clears
    // the custom pivot (drag-to-centre = reset).
    const home = this.pivotHomeFor(drag.owner);
    const atHome =
      home != null &&
      Math.hypot(drag.pivot.x - home.x, drag.pivot.y - home.y) < 0.01;
    const nextPivot = atHome ? undefined : { x: drag.pivot.x, y: drag.pivot.y };
    if (drag.owner.kind === "line") {
      const lineId = drag.owner.lineId;
      this.host.commit("Move rotation pivot", (proj) => ({
        ...proj,
        constructionLines: projectConstructionLines(proj).map((l) =>
          l.id === lineId ? { ...l, pivotPoint: nextPivot } : l,
        ),
      }));
    } else if (drag.owner.kind === "image") {
      const imageId = drag.owner.imageId;
      this.host.commit("Move rotation pivot", (proj) => ({
        ...proj,
        tracingImages: projectTracingImages(proj).map((img) =>
          img.id === imageId ? { ...img, pivotPoint: nextPivot } : img,
        ),
      }));
    } else {
      const groupId = drag.owner.groupId;
      this.host.commit("Move rotation pivot", (proj) => ({
        ...proj,
        constructionGroups: (proj.constructionGroups ?? []).map((g) =>
          g.id === groupId ? { ...g, pivotPoint: nextPivot } : g,
        ),
      }));
    }
  }

  private cancelPivotDrag(): void {
    if (!this.pivotDrag) return;
    this.pivotDrag = null;
    this.host.getScene().setTransientPivot(null);
    setCanvasCursor(this.host.getScope(), "");
    this.host.rerender();
  }

  // -------------------------------------------------------------------
  // Panel scrubber entry point
  // -------------------------------------------------------------------

  /** Apply a specific absolute rotation (degrees, normalised to
   *  (-180, 180]) to one construction line. Used by the rotation
   *  scrubber in the properties panel — computes the delta from the
   *  line's current `rotation` and commits one combined rotation
   *  step. No-op when the value is effectively unchanged. */
  applyConstructionLineRotation(lineId: string, targetDeg: number): void {
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === lineId,
    );
    if (!line) return;
    const target = normalizeRotation(targetDeg);
    const current = normalizeRotation(line.rotation ?? 0);
    let delta = target - current;
    while (delta > 180) delta -= 360;
    while (delta < -180) delta += 360;
    if (Math.abs(delta) < 0.01) return;
    const scope = this.host.getScope();
    const bounds = getUnrotatedBounds(line, scope);
    if (!bounds) return;
    // Honour a user-positioned pivot (issue #420) so the panel
    // scrubber spins about the same point as the canvas gesture.
    // With no custom pivot, keep the historical behaviour (rotate
    // about the stored rotationCenter / bbox centre).
    const cx = line.pivotPoint ? line.pivotPoint.x : bounds.cx;
    const cy = line.pivotPoint ? line.pivotPoint.y : bounds.cy;
    this.host.commit("Set rotation", (proj) =>
      withSnappedPartnersFollowing(
        proj,
        projectConstructionLines(proj).map((l) =>
          l.id === lineId ? rotateConstructionLine(l, delta, cx, cy, scope) : l,
        ),
        [lineId],
        scope,
      ),
    );
  }

  private tryRouteMirrorRotationToSource(
    hitLineId: string,
    pivot: { x: number; y: number },
  ): {
    sourceRootId: string;
    sourcePivot: { x: number; y: number };
    sign: 1 | -1;
  } | null {
    return routeMirrorRotationToSource(
      this.host.getProject(),
      hitLineId,
      pivot,
      this.host.getScope(),
    );
  }
}
